import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Loader2, KeyRound } from 'lucide-react';
import { integrations, bitwarden, verbund } from '../api';
import type { IntegrationToken } from '../api';
import { format } from 'date-fns';

/**
 * Bei welchen Einträgen die eigene Adresse hilft.
 *
 * Nur bei der Verwaltungsliste: dort ist die Frage „unter welcher Adresse komme
 * ich hier eigentlich an" die erste, die sich stellt — und die einzige, die
 * niemand ohne Hilfsmittel beantworten kann. Bei einem API-Token wäre der
 * Hinweis nur Rauschen.
 */
const ADRESSLISTEN = ['SSH_ALLOWLIST'];

function TokenRow({ token, deineAdresse }: { token: IntegrationToken; deineAdresse: string | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const istAdressliste = ADRESSLISTEN.includes(token.key);
  const zeigtAdresse = istAdressliste && Boolean(deineAdresse);
  /** Steht sie schon im gerade getippten Wert? Die Anmerkung zählt dabei nicht mit. */
  const schonDrin = deineAdresse
    ? value
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map((t) => t.split('@')[0])
        .includes(deineAdresse)
    : false;

  /** Heute plus `tage`, als JJJJ-MM-TT. */
  const inTagen = (tage: number) => {
    const d = new Date();
    d.setDate(d.getDate() + tage);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const anhaengen = (zusatz: string) =>
    setValue((v) => (v.trim() ? `${v.trim()} ${zusatz}` : zusatz));

  const setMutation = useMutation({
    mutationFn: (v: string) => integrations.set(token.key, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      setEditing(false);
      setValue('');
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Speichern fehlgeschlagen.';
      setError(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => integrations.remove(token.key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });

  return (
    <div className="bg-surface border border-slate-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {token.hasValue ? (
              <CheckCircle size={16} className="text-emerald-600" />
            ) : (
              <Circle size={16} className="text-slate-300" />
            )}
            <h3 className="text-sm font-semibold text-slate-800">{token.label}</h3>
          </div>
          {token.description && (
            <p className="text-xs text-slate-400 mt-1">{token.description}</p>
          )}
          <p className="text-xs text-slate-400 mt-1 font-mono">{token.key}</p>
          {token.source === 'environment' && (
            // Otherwise this reads "nicht gesetzt" for something that is
            // demonstrably working, and someone goes hunting for a fault that
            // is not there.
            <p className="text-xs text-amber-700 mt-1">
              Kommt aus der Umgebung des Containers, nicht von hier. Hinterlegen überschreibt
              den Wert und macht ihn hier änderbar.
            </p>
          )}
          {token.source === 'database' && token.updatedAt && (
            <p className="text-xs text-slate-400 mt-1">
              Zuletzt aktualisiert: {format(new Date(token.updatedAt), 'PPp')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {token.source === 'database' && !editing && (
            <button
              onClick={() => {
                if (confirm(`${token.label}-Token entfernen?`)) removeMutation.mutate();
              }}
              disabled={removeMutation.isPending}
              className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-40"
            >
              Entfernen
            </button>
          )}
          {!editing && (
            <button
              onClick={() => { setEditing(true); setError(''); setValue(token.offen ? (token.wert ?? '') : ''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-900 hover:bg-slate-800 text-surface rounded-lg"
            >
              <KeyRound size={12} />
              {token.hasValue ? (token.aktionLabel ?? 'Ersetzen') : (token.aktionLabel ?? 'Token hinterlegen')}
            </button>
          )}
        </div>
      </div>

      {editing && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            setMutation.mutate(value);
          }}
          className={`mt-4 flex gap-2 border-t border-slate-100 pt-4 ${token.mehrzeilig ? 'flex-col items-stretch' : ''}`}
        >
          {error && (
            <div className="w-full bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-2">
              {error}
            </div>
          )}
          {token.mehrzeilig ? (
            // Sichtbar statt maskiert: eine JSON-Datei blind einzufuegen und
            // nicht sehen zu koennen, ob sie vollstaendig ist, waere die
            // schlechtere Sicherheit -- der haeufigste Fehler ist hier ein
            // halber Inhalt, kein Mitleser.
            <textarea
              autoFocus
              required
              rows={6}
              placeholder='{ "type": "service_account", "client_email": "…", "private_key": "…" }'
              value={value}
              onChange={(e) => setValue(e.target.value)}
              spellCheck={false}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          ) : token.offen ? (
            // Sichtbar und vorbelegt: das hier ist kein Geheimnis, sondern
            // eine Liste, die ergänzt wird. Sie maskiert und leer anzubieten
            // hiesse, sie jedes Mal blind neu zu tippen — und irgendwann fehlt
            // die Adresse des PC, der sich damit selbst aussperrt.
            <input
              autoFocus
              required
              type="text"
              placeholder="72.61.89.48 203.0.113.0/24"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              spellCheck={false}
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          ) : (
            <input
              autoFocus
              required
              type="password"
              placeholder="API-Token einfügen"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="new-password"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          )}
          <button
            type="submit"
            disabled={setMutation.isPending}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60 flex items-center gap-2"
          >
            {setMutation.isPending && <Loader2 size={14} className="animate-spin" />}
            Speichern
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setValue(''); setError(''); }}
            className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
          >
            Abbrechen
          </button>
        </form>
      )}

      {/* ══ DIE LISTE IM RUHEZUSTAND ═══════════════════════════════════════
          Der Wert steht sonst nur im Formular, und dort sieht man ihn als eine
          Zeile Text, in der zwanzig Adressen hintereinanderstehen. Hier ist
          auf einen Blick erkennbar, welcher Eintrag dauerhaft ist und welcher
          ein Ablaufdatum hat. */}
      {istAdressliste && !editing && (token.adressen?.length ?? 0) > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
          {token.adressen!.map((a) => (
            <span
              key={`${a.adresse}@${a.ablauf ?? ''}`}
              title={
                a.ablauf
                  ? `Befristet bis einschliesslich ${a.ablauf}`
                  : 'Dauerhaft — faellt nie von selbst heraus'
              }
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-xs ${
                a.ablauf
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              {a.adresse}
              {a.ablauf && <span className="font-sans text-[10px]">bis {a.ablauf}</span>}
            </span>
          ))}
        </div>
      )}

      {/* ══ DEINE ADRESSE ══════════════════════════════════════════════════
          Unter welcher Adresse jemand hier ankommt, kann er ohne Hilfsmittel
          nicht wissen — der PC dagegen schon, er beantwortet gerade eine
          Anfrage von dort.

          Der Knopf HÄNGT AN, er ersetzt nicht. Das ist der ganze Punkt: wer
          die Liste überschreibt, verliert irgendwann 72.61.89.48 — und dann
          kommt der Provisioning Center beim nächsten „Firewall auffrischen"
          nicht mehr auf den Server, den er verwaltet. */}
      {editing && zeigtAdresse && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>
            Du kommst hier an als <code className="font-mono text-slate-700">{deineAdresse}</code>
          </span>
          {schonDrin ? (
            <span className="text-emerald-700">— steht schon in der Liste</span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => anhaengen(`${deineAdresse}@${inTagen(30)}`)}
                className="rounded border border-slate-300 px-2 py-0.5 font-medium text-slate-700 hover:bg-slate-50"
              >
                für 30 Tage anhängen
              </button>
              <button
                type="button"
                onClick={() => anhaengen(deineAdresse!)}
                className="rounded border border-slate-200 px-2 py-0.5 text-slate-500 hover:bg-slate-50"
              >
                dauerhaft
              </button>
            </>
          )}
          <span className="w-full text-slate-400">
            Ein Eintrag gilt dauerhaft, solange kein Datum dahinter steht: <code className="font-mono">1.2.3.4@2026-10-11</code>{' '}
            fällt am 12.10. von selbst heraus. Dauerhaft gehören die Adresse des PC und feste
            Standorte — ein Arbeitsplatz mit wechselnder Adresse besser befristet.
          </span>
          <span className="w-full text-slate-400">
            Wirkt erst, wenn je Server unter „Hostprüfung" die Firewall aufgefrischt wird —
            eine Änderung hier rollt sich nicht von selbst aus.
          </span>
        </div>
      )}
    </div>
  );
}

export default function IntegrationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: integrations.list,
  });

  // Grouped in the order the backend lists them — twenty entries in one flat
  // list is a wall nobody reads.
  const eintraege = data?.eintraege ?? [];
  const groups = eintraege.length
    ? [...new Set(eintraege.map((t) => t.group))].map((group) => ({
        group,
        tokens: eintraege.filter((t) => t.group === group),
      }))
    : [];

  const missing = eintraege.filter((t) => !t.hasValue).length;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-slate-500">
          Zugangsdaten zu allem, was der PC von außen anspricht — verschlüsselt gespeichert,
          Werte werden nie wieder angezeigt. Änderungen wirken sofort, ohne Neustart.
        </p>
        {missing > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            {missing} von {eintraege.length} nicht hinterlegt. Nicht jede davon wird gebraucht —
            die Beschreibung sagt, was ohne sie ausfällt.
          </p>
        )}
        <p className="mt-2 text-xs leading-snug text-slate-400">
          Nicht hier, mit Absicht: der Aktivierungs-Signaturschlüssel, der Hauptschlüssel der
          Verschlüsselung und die Datenbank-Zugangsdaten. Die stehen in der Umgebung des
          Deployments — ein Schlüssel, mit dem dieser Bereich selbst entschlüsselt wird, kann
          nicht in ihm liegen.
        </p>
      </div>

      {isLoading
        ? Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />
          ))
        : groups.map(({ group, tokens }) => (
            <div key={group}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{group}</h2>
                {group === 'Bitwarden' && <BitwardenPruefen />}
                {tokens.some((t) => t.key === 'VERBUND_CP_URL') && <VerbundPruefen />}
              </div>
              <div className="space-y-3">
                {tokens.map((token) => (
                  <TokenRow key={token.key} token={token} deineAdresse={data?.deineAdresse ?? null} />
                ))}
              </div>
            </div>
          ))}
    </div>
  );
}

/**
 * Bitwarden: Anmeldung, Entsperren und Sammlung tatsächlich prüfen — die
 * Einträge darüber sagen nur, ob etwas hinterlegt ist, nicht, ob es stimmt.
 */
function BitwardenPruefen() {
  const qc = useQueryClient();
  const pruefung = useMutation({
    mutationFn: bitwarden.status,
    onSuccess: (s) => qc.setQueryData(['bitwarden', 'status'], s),
  });
  const s = pruefung.data;
  return (
    <div className="flex min-w-0 items-center gap-2">
      {s && (
        <span className={`truncate text-xs ${s.ok ? 'text-emerald-700' : 'text-amber-700'}`} title={s.meldung}>
          {s.meldung}
        </span>
      )}
      <button
        type="button"
        onClick={() => pruefung.mutate()}
        disabled={pruefung.isPending}
        className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {pruefung.isPending ? 'prüft …' : 'Verbindung prüfen'}
      </button>
    </div>
  );
}

/**
 * Verlagsverbund: ein echter Aufruf der Admin-API der Control Plane. Die
 * Einträge darüber sagen nur, ob etwas hinterlegt ist — eine erreichbare CP
 * mit falschem Token ist die häufigere Störung, und die zeigt nur der Aufruf.
 */
function VerbundPruefen() {
  const qc = useQueryClient();
  const pruefung = useMutation({
    mutationFn: verbund.status,
    onSuccess: (s) => qc.setQueryData(['verbund', 'status'], s),
  });
  const s = pruefung.data;
  const meldung = pruefung.error
    ? 'Prüfung fehlgeschlagen.'
    : !s
      ? null
      : !s.eingerichtet
        ? 'Adresse oder Verwaltungs-Token fehlt.'
        : s.erreichbar
          ? `Verbunden mit ${s.cpUrl} — Token angenommen.`
          : (s.fehler ?? 'Nicht erreichbar.');
  const ok = Boolean(s?.eingerichtet && s.erreichbar);
  return (
    <div className="flex min-w-0 items-center gap-2">
      {meldung && (
        <span className={`truncate text-xs ${ok ? 'text-emerald-700' : 'text-amber-700'}`} title={meldung}>
          {meldung}
        </span>
      )}
      <button
        type="button"
        onClick={() => pruefung.mutate()}
        disabled={pruefung.isPending}
        className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {pruefung.isPending ? 'prüft …' : 'Verbindung prüfen'}
      </button>
    </div>
  );
}
